<template>
  <div class="edit-pet-page">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('UsersFavouritesPage')">My Favourites</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title">Edit Pet</h1>
      <p class="page-sub">Update your pet's profile details</p>
    </div>

    <div class="page-wrapper">
      <div class="form-card">
        <form @submit.prevent="updatePet">

          <div class="form-group">
            <label class="form-label">Name</label>
            <input v-model="form.pet_name" type="text" class="form-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">Breed</label>
            <input v-model="form.breed" type="text" class="form-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">Birthday</label>
            <input v-model="form.birthday" type="date" class="form-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">Age Group</label>
            <div class="pill-group">
              <label v-for="option in ['Puppy', 'Adult', 'Senior']" :key="option"
                class="pill-option" :class="{ 'pill-active': form.age === option }">
                <input type="radio" v-model="form.age" :value="option" hidden required />
                {{ option }}
              </label>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Gender</label>
            <div class="pill-group">
              <label class="pill-option pill-male" :class="{ 'pill-active': form.gender === 'Male' }">
                <input type="radio" v-model="form.gender" value="Male" hidden />
                Male
              </label>
              <label class="pill-option pill-female" :class="{ 'pill-active': form.gender === 'Female' }">
                <input type="radio" v-model="form.gender" value="Female" hidden />
                Female
              </label>
            </div>
          </div>

          <div class="form-group checkbox-row">
            <input v-model="form.vaccinated" type="checkbox" class="checkbox-input" id="vaccinated" />
            <label class="checkbox-label" for="vaccinated">Vaccinated</label>
          </div>

          <div class="form-group">
            <label class="form-label">Description</label>
            <textarea v-model="form.description" class="form-textarea"></textarea>
          </div>

          <div class="form-group">
            <label class="form-label">Available Days</label>
            <div class="days-group">
              <label
                v-for="day in weekdays"
                :key="day"
                class="day-pill"
                :class="{ 'day-active': form.available_days.includes(day) }"
              >
                <input type="checkbox" :value="day" v-model="form.available_days" hidden />
                {{ day.slice(0, 3) }}
              </label>
            </div>
          </div>

          <div class="form-group time-row">
            <div class="time-col">
              <label class="form-label">From</label>
              <input v-model="form.available_start_time" type="time" class="form-input" required />
            </div>
            <div class="time-sep">—</div>
            <div class="time-col">
              <label class="form-label">To</label>
              <input v-model="form.available_end_time" type="time" class="form-input" required />
            </div>
          </div>

          <button type="submit" class="submit-btn">Save Changes</button>

        </form>

        <div v-if="successMessage" class="feedback success">{{ successMessage }}</div>
        <div v-if="errorMessage" class="feedback error">{{ errorMessage }}</div>

      </div>
    </div>
  </div>
</template>


<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'EditPetDashboard',
  props: {
    pet: {
      type: Object,
      default: () => ({})
    },
    user: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'changePage', 'pet-updated'],
  data() {
    return {
      weekdays: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
      form: {
        pet_name:      this.pet.pet_name,
        breed:         this.pet.breed,
        birthday:      this.pet.birthday || '',
        vaccinated:    this.pet.vaccinated || false,
        age:           this.pet.age,
        gender:        this.pet.gender,
        description:   this.pet.description,
        available_days: this.pet.available_days
          ? (Array.isArray(this.pet.available_days)
            ? [...this.pet.available_days]
            : this.pet.available_days.split(',').map(day => day.trim()))
          : [],
        available_start_time:    this.pet.available_start_time || '09:00',
        available_end_time:      this.pet.available_end_time   || '17:00'
      },
      successMessage: '',
      errorMessage: '',
      sidebarOpen: false,
      petsOpen: false,
    };
  },
  methods: {
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    async updatePet() {
      // ensure at least one day selected
      if (!this.form.available_days.length) {
        this.errorMessage = 'Please select at least one available day.';
        return;
      }
      // ensure valid time range
      if (this.form.available_end_time <= this.form.available_start_time) {
        this.errorMessage = 'End time must be after start time.';
        return;
      }
      try {
        const response = await axios.patch(
          `${backendUrl}/api/pets/${this.pet.id}`,
          this.form,
          { withCredentials: true }
        );
        this.successMessage = response.data.message || 'Pet updated successfully';
        this.errorMessage = '';
        this.$emit('pet-updated', response.data.pet || this.form);
      } catch (error) {
        this.errorMessage = error.response?.data?.error || 'Failed to update pet';
        this.successMessage = '';
      }
    }
  },
  watch: {
    user: {
      handler(newVal) {
        console.log("User updated:", newVal);
      },
      immediate: true,
      deep: true
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.edit-pet-page {
  min-height: 100vh;
  background: #fffbe8;
}

/* ── Page header ── */
.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 20px 0 24px;
}

.page-title {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
}

.page-sub {
  font-size: 14px;
  color: #aaa;
  font-weight: 500;
  margin-top: 4px;
}

/* ── Page wrapper ── */
.page-wrapper {
  max-width: 560px;
  margin: 0 auto;
  padding: 24px 20px 60px;
}

/* ── Form card ── */
.form-card {
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid #f0e0ff;
  padding: 28px 24px;
  box-shadow: 0 4px 24px rgba(186, 113, 255, 0.08);
}

/* ── Form groups ── */
.form-group {
  margin-bottom: 22px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 13px 14px;
  border: 1.5px solid #f0e0ff;
  border-radius: 14px;
  font-size: 15px;
  background: #fffbe8;
  outline: none;
  transition: all 0.2s ease;
  color: #1a1a1a;
  font-family: inherit;
}

.form-input:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

.form-textarea {
  width: 100%;
  padding: 13px 14px;
  border: 1.5px solid #f0e0ff;
  border-radius: 14px;
  font-size: 15px;
  background: #fffbe8;
  outline: none;
  resize: vertical;
  min-height: 110px;
  font-family: inherit;
  color: #1a1a1a;
  transition: all 0.2s ease;
}

.form-textarea:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

/* ── Pill selectors ── */
.pill-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pill-option {
  flex: 1;
  text-align: center;
  padding: 9px 18px;
  border-radius: 999px;
  border: 1.5px solid #f0e0ff;
  background: #fffbe8;
  color: #888;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  user-select: none;
}

.pill-option:hover { border-color: #ba71ff; color: #4a1070; }

.pill-option.pill-active {
  background: #eac7ff;
  border-color: #ba71ff;
  color: #4a1070;
}

.pill-male.pill-active   { background: #eff6ff; border-color: #2563eb; color: #1d4ed8; }
.pill-female.pill-active { background: #fdf2f8; border-color: #db2777; color: #be185d; }

/* ── Days group ── */
.days-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.day-pill {
  padding: 8px 12px;
  border-radius: 999px;
  border: 1.5px solid #f0e0ff;
  background: #fffbe8;
  color: #888;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  user-select: none;
}

.day-pill:hover { border-color: #ba71ff; color: #4a1070; }

.day-active {
  background: #eac7ff;
  border-color: #ba71ff;
  color: #4a1070;
}

/* ── Time row ── */
.time-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.time-col {
  flex: 1;
}

.time-sep {
  font-size: 18px;
  color: #ccc;
  font-weight: 700;
  padding-bottom: 14px;
  flex-shrink: 0;
}

/* ── Checkbox ── */
.checkbox-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.checkbox-input {
  width: 18px;
  height: 18px;
  accent-color: #ba71ff;
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-label {
  font-size: 14px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
}

/* ── Submit button ── */
.submit-btn {
  width: 100%;
  padding: 14px;
  background: #eac7ff;
  color: #4a1070;
  border: none;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
  margin-top: 6px;
  font-family: inherit;
}

.submit-btn:hover  { background: #ba71ff; color: #fff; }
.submit-btn:active { transform: scale(0.98); }

/* ── Feedback ── */
.feedback {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
}

.success { background: #d4f5e2; color: #1a6b3c; }
.error   { background: #fde8e8; color: #991b1b; }

/* ── Responsive ── */
@media (max-width: 480px) {
  .page-wrapper { padding: 20px 14px 40px; }
  .form-card { padding: 20px 16px; }
  .time-row { flex-direction: column; gap: 0; }
  .time-sep { display: none; }
}
</style>